'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import posthog from 'posthog-js'
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react'
import { useEffect, Suspense, createContext, useContext, useState } from 'react'
import { useSafeSearchParams } from '@/components/SearchParamsProvider'

// Declare posthog on the window object for TypeScript
declare global {
  interface Window {
    posthog?: typeof posthog
  }
}

// Using enums for feature flag names as required
export enum FeatureFlag {
  // Add your feature flags here
  EXAMPLE_FEATURE = 'example-feature',
}

// Debug logger for PostHog
const debugLog = (message: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[PostHog Debug] ${message}`, data || '');
  }
};

// PostHog configuration from environment variables
const posthogApiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

// Feature flag context to provide flags throughout the app
type FeatureFlagsContextType = {
  flags: Record<string, boolean | string | number | undefined>;
  isLoading: boolean;
  isEnabled: (flag: FeatureFlag) => boolean;
  getValue: (flag: FeatureFlag) => string | boolean | number | undefined;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  flags: {},
  isLoading: true,
  isEnabled: () => false,
  getValue: () => undefined,
});

export const useFeatureFlags = () => useContext(FeatureFlagsContext);

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean | string | number | undefined>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!posthogApiKey) {
      setIsLoading(false);
      return;
    }

    posthog.init(posthogApiKey, {
      api_host: '/ingest',
      ui_host: 'https://us.posthog.com',
      capture_pageview: false, // We capture pageviews manually
      capture_pageleave: true, // Enable pageleave capture
      debug: process.env.NODE_ENV === 'development',
      // Disable the session recording features to prevent recursion issues
      disable_session_recording: true,
      loaded: (posthogInstance) => {
        // Load feature flags after PostHog is fully initialized
        posthogInstance.onFeatureFlags((flags) => {
          const processedFlags: Record<string, boolean | string | number | undefined> = {};
          
          // Convert array of flags to object for easier access
          Object.values(FeatureFlag).forEach(flagName => {
            processedFlags[flagName] = posthog.isFeatureEnabled(flagName);
          });
          
          setFeatureFlags(processedFlags);
          setIsLoading(false);
          debugLog('Feature flags loaded', processedFlags);
        });
      }
    });
  }, []);

  const isEnabled = (flag: FeatureFlag): boolean => {
    return !!featureFlags[flag];
  };

  const getValue = (flag: FeatureFlag): string | boolean | number | undefined => {
    return featureFlags[flag];
  };

  const value = {
    flags: featureFlags,
    isLoading,
    isEnabled,
    getValue,
  };

  return (
    <PHProvider client={posthog}>
      <FeatureFlagsContext.Provider value={value}>
        <SuspendedPostHogPageView />
        {children}
      </FeatureFlagsContext.Provider>
    </PHProvider>
  );
}

function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSafeSearchParams();
  const posthogClient = usePostHog();
  
  // Create a ref to track if we've already sent a pageview for this URL
  const [lastTrackedUrl, setLastTrackedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (pathname && posthogClient) {
      let url = window.origin + pathname;
      const search = searchParams ? searchParams.toString() : '';
      if (search) {
        url += '?' + search;
      }
      
      // Only capture pageview if the URL has changed and we haven't tracked it yet
      if (url !== lastTrackedUrl) {
        setLastTrackedUrl(url);
        posthogClient.capture('$pageview', { 
          '$current_url': url,
          // Disable RRWeb custom events for pageviews to prevent recursion
          $recording_disabled: true
        });
      }
    }
  }, [pathname, searchParams, posthogClient, lastTrackedUrl]);

  return null;
}

function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}

// Utility functions for analytics with error handling
export const Analytics = {
  // Track event with properties
  track: (eventName: string, properties?: Record<string, any>) => {
    if (!posthogApiKey) {
      debugLog(`⚠️ Skipped tracking "${eventName}": PostHog API key is missing`);
      return;
    }

    try {
      debugLog(`Tracking event: ${eventName}`, properties);
      posthog.capture(eventName, properties);
    } catch (error) {
      debugLog(`❌ Error tracking "${eventName}":`, error);
    }
  },

  // Identify user
  identify: (userId: string, traits?: Record<string, any>) => {
    if (!posthogApiKey) {
      debugLog('⚠️ Skipped identify: PostHog API key is missing');
      return;
    }

    try {
      debugLog(`Identifying user: ${userId.substring(0, 4)}...`, traits);
      posthog.identify(userId, traits);
    } catch (error) {
      debugLog('❌ Error identifying user:', error);
    }
  },

  // Reset user (logout)
  reset: () => {
    if (!posthogApiKey) return;

    try {
      debugLog('Resetting user identity');
      posthog.reset();
    } catch (error) {
      debugLog('❌ Error resetting user:', error);
    }
  },

  // Register persistent super properties
  register: (properties: Record<string, any>) => {
    if (!posthogApiKey) {
      debugLog('⚠️ Skipped register: PostHog API key is missing');
      return;
    }

    try {
      debugLog('Registering super properties', properties);
      posthog.register(properties);
    } catch (error) {
      debugLog('❌ Error registering properties:', error);
    }
  },
};

// Export posthog instance for direct access if needed
export { posthog };
